import { useState, useEffect, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Check,
  X,
  Copy,
  ExternalLink,
  Store,
  UtensilsCrossed,
  Coffee,
  Wrench,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Building2,
  User,
  Globe,
  Sparkles,
  Zap,
  Crown,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PlanKey = "basic" | "pro" | "enterprise";
type BusinessTypeKey = "store" | "restaurant" | "cafe" | "services" | "other";

interface Plan {
  readonly key: PlanKey;
  readonly label: string;
  readonly price: string;
  readonly amount: number;
  readonly icon: ReactNode;
  readonly recommended: boolean;
  readonly features: ReadonlyArray<string>;
}

interface BusinessType {
  readonly key: BusinessTypeKey;
  readonly label: string;
  readonly icon: ReactNode;
}

const PLANS: ReadonlyArray<Plan> = [
  {
    key: "basic",
    label: "Basic",
    price: "₪149",
    amount: 14900,
    icon: <Zap className="h-5 w-5" />,
    recommended: false,
    features: ["עד 100 מוצרים", "תמיכה בסיסית", "דומיין pages.dev", "אנליטיקס בסיסי"],
  },
  {
    key: "pro",
    label: "Pro",
    price: "₪249",
    amount: 24900,
    icon: <Sparkles className="h-5 w-5" />,
    recommended: true,
    features: ["מוצרים ללא הגבלה", "תמיכה מועדפת", "דומיין מותאם אישית", "אנליטיקס מתקדם", "כפתורי WhatsApp"],
  },
  {
    key: "enterprise",
    label: "Enterprise",
    price: "₪499",
    amount: 49900,
    icon: <Crown className="h-5 w-5" />,
    recommended: false,
    features: ["הכל ב-Pro", "מנהל חשבון ייעודי", "SLA 99.9%", "אינטגרציות API", "לוגו מותאם אישית"],
  },
] as const;

const BUSINESS_TYPES: ReadonlyArray<BusinessType> = [
  { key: "store", label: "חנות אונליין", icon: <Store className="h-6 w-6" /> },
  { key: "restaurant", label: "מסעדה", icon: <UtensilsCrossed className="h-6 w-6" /> },
  { key: "cafe", label: "בית קפה", icon: <Coffee className="h-6 w-6" /> },
  { key: "services", label: "שירותים", icon: <Wrench className="h-6 w-6" /> },
  { key: "other", label: "אחר", icon: <MoreHorizontal className="h-6 w-6" /> },
] as const;

interface ProvisionStep {
  readonly name: string;
  readonly status: "pending" | "done" | "failed";
  readonly detail?: string;
}

interface ProvisionResult {
  readonly tenantId: string;
  readonly slug: string;
  readonly dbId: string;
  readonly adminUrl: string;
  readonly siteUrl: string;
  readonly steps: ReadonlyArray<ProvisionStep>;
}

type Partner = {
  readonly id: string;
  readonly name: string;
};

const STEP_LABELS: Record<string, string> = {
  "create-d1": "יצירת בסיס נתונים...",
  "seed-kv": "הגדרת הגדרות...",
  "create-r2-folder": "יצירת אחסון...",
  "create-pages": "הגדרת דומיין...",
  "create-umami": "הגדרת אנליטיקס...",
  "insert-tenant": "יצירת דייר...",
  "create-referral": "יצירת הפניה...",
};

// ---------------------------------------------------------------------------
// Wizard step config
// ---------------------------------------------------------------------------

const WIZARD_STEPS = [
  { id: 1, label: "פרטי עסק", icon: <Building2 className="h-4 w-4" /> },
  { id: 2, label: "תוכנית", icon: <Sparkles className="h-4 w-4" /> },
  { id: 3, label: "בעלים", icon: <User className="h-4 w-4" /> },
  { id: 4, label: "דומיין", icon: <Globe className="h-4 w-4" /> },
  { id: 5, label: "סיכום", icon: <Check className="h-4 w-4" /> },
] as const;

// ---------------------------------------------------------------------------
// Subdomain sanitizer — only allows English letters, numbers, hyphens
// ---------------------------------------------------------------------------

function sanitizeSubdomain(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StepperProps {
  readonly currentStep: number;
}

function Stepper({ currentStep }: StepperProps): ReactNode {
  return (
    <div className="flex items-center justify-center gap-0 mb-8" dir="ltr">
      {WIZARD_STEPS.map((step, idx) => {
        const isCompleted = step.id < currentStep;
        const isActive = step.id === currentStep;
        return (
          <div key={step.id} className="flex items-center">
            {/* Step circle */}
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={[
                  "flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all duration-300",
                  isCompleted
                    ? "border-primary bg-primary text-primary-foreground"
                    : isActive
                    ? "border-primary bg-primary/10 text-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]"
                    : "border-border bg-card text-muted-foreground",
                ].join(" ")}
              >
                {isCompleted ? <Check className="h-4 w-4" /> : step.icon}
              </div>
              <span
                className={[
                  "text-xs font-medium whitespace-nowrap",
                  isActive ? "text-primary" : "text-muted-foreground",
                ].join(" ")}
              >
                {step.label}
              </span>
            </div>
            {/* Connector */}
            {idx < WIZARD_STEPS.length - 1 && (
              <div
                className={[
                  "h-0.5 w-12 mx-1 mb-5 rounded-full transition-all duration-500",
                  step.id < currentStep
                    ? "bg-gradient-to-r from-primary to-primary/60"
                    : "bg-border",
                ].join(" ")}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Business Info
// ---------------------------------------------------------------------------

interface Step1Props {
  readonly storeName: string;
  readonly businessType: BusinessTypeKey | "";
  readonly onNameChange: (v: string) => void;
  readonly onTypeChange: (v: BusinessTypeKey) => void;
  readonly onNext: () => void;
}

function Step1BusinessInfo({ storeName, businessType, onNameChange, onTypeChange, onNext }: Step1Props): ReactNode {
  const canProceed = storeName.trim().length > 0 && businessType !== "";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">פרטי העסק</CardTitle>
        <CardDescription>מה שם העסק ומה סוגו?</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Store name */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-foreground">שם העסק</label>
          <Input
            value={storeName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="למשל: הפרחים של נדין"
            className="min-h-[48px] text-base"
            autoFocus
          />
        </div>

        {/* Business type grid */}
        <div className="space-y-2">
          <label className="text-sm font-semibold text-foreground">סוג עסק</label>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
            {BUSINESS_TYPES.map((bt) => (
              <button
                key={bt.key}
                type="button"
                onClick={() => onTypeChange(bt.key)}
                className={[
                  "flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-sm font-medium transition-all duration-200 hover:border-primary/60 hover:bg-primary/5 min-h-[44px] cursor-pointer",
                  businessType === bt.key
                    ? "border-primary bg-primary/10 text-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
                    : "border-border bg-card text-muted-foreground",
                ].join(" ")}
              >
                <span className={businessType === bt.key ? "text-primary" : "text-muted-foreground"}>
                  {bt.icon}
                </span>
                <span>{bt.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-start pt-2">
          <Button onClick={onNext} disabled={!canProceed} size="lg" className="gap-2">
            המשך
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Plan Selection
// ---------------------------------------------------------------------------

interface Step2Props {
  readonly selectedPlan: PlanKey | "";
  readonly onPlanChange: (v: PlanKey) => void;
  readonly onNext: () => void;
  readonly onBack: () => void;
}

function Step2PlanSelection({ selectedPlan, onPlanChange, onNext, onBack }: Step2Props): ReactNode {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">בחירת תוכנית</CardTitle>
        <CardDescription>איזו תוכנית מתאימה לעסק הזה?</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-3">
          {PLANS.map((plan) => (
            <button
              key={plan.key}
              type="button"
              onClick={() => onPlanChange(plan.key)}
              className={[
                "relative flex flex-col rounded-xl border-2 p-5 text-right transition-all duration-200 hover:border-primary/60 cursor-pointer min-h-[44px]",
                selectedPlan === plan.key
                  ? "border-primary bg-primary/5 shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
                  : "border-border bg-card hover:bg-accent/30",
              ].join(" ")}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge variant="default" className="text-xs px-3 py-0.5 shadow-sm">
                    מומלץ
                  </Badge>
                </div>
              )}

              <div className={[
                "mb-3 flex h-10 w-10 items-center justify-center rounded-lg",
                selectedPlan === plan.key ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground",
              ].join(" ")}>
                {plan.icon}
              </div>

              <div className="mb-1 text-lg font-bold text-foreground">{plan.label}</div>
              <div className="mb-4 text-2xl font-extrabold text-primary">
                {plan.price}
                <span className="text-sm font-normal text-muted-foreground">/חודש</span>
              </div>

              <ul className="space-y-1.5 text-right">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Check className="h-3.5 w-3.5 shrink-0 text-green-500" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {selectedPlan === plan.key && (
                <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-primary">
                  <Check className="h-3.5 w-3.5" />
                  נבחר
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={onBack} size="lg" className="gap-2">
            <ChevronRight className="h-4 w-4" />
            חזור
          </Button>
          <Button onClick={onNext} disabled={!selectedPlan} size="lg" className="gap-2">
            המשך
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Owner Details
// ---------------------------------------------------------------------------

interface Step3Props {
  readonly ownerName: string;
  readonly ownerEmail: string;
  readonly ownerPhone: string;
  readonly onNameChange: (v: string) => void;
  readonly onEmailChange: (v: string) => void;
  readonly onPhoneChange: (v: string) => void;
  readonly onNext: () => void;
  readonly onBack: () => void;
}

function Step3OwnerDetails({
  ownerName, ownerEmail, ownerPhone,
  onNameChange, onEmailChange, onPhoneChange,
  onNext, onBack,
}: Step3Props): ReactNode {
  const canProceed = ownerName.trim().length > 0 && ownerEmail.trim().length > 0 && ownerPhone.trim().length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">פרטי הבעלים</CardTitle>
        <CardDescription>מי הבעלים של העסק הזה?</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-foreground">שם מלא</label>
          <Input
            value={ownerName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="ישראל ישראלי"
            className="min-h-[48px] text-base"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-foreground">אימייל</label>
          <Input
            type="email"
            value={ownerEmail}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="owner@example.com"
            className="min-h-[48px] text-base"
            dir="ltr"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-foreground">טלפון</label>
          <Input
            value={ownerPhone}
            onChange={(e) => onPhoneChange(e.target.value)}
            placeholder="0501234567"
            className="min-h-[48px] text-base"
            dir="ltr"
          />
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={onBack} size="lg" className="gap-2">
            <ChevronRight className="h-4 w-4" />
            חזור
          </Button>
          <Button onClick={onNext} disabled={!canProceed} size="lg" className="gap-2">
            המשך
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Domain & Partner
// ---------------------------------------------------------------------------

interface Step4Props {
  readonly domain: string;
  readonly partnerId: string;
  readonly partners: ReadonlyArray<Partner>;
  readonly onDomainChange: (v: string) => void;
  readonly onPartnerChange: (v: string) => void;
  readonly onNext: () => void;
  readonly onBack: () => void;
}

function Step4DomainPartner({
  domain, partnerId, partners,
  onDomainChange, onPartnerChange,
  onNext, onBack,
}: Step4Props): ReactNode {
  const canProceed = domain.trim().length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">כתובת האתר ושותף</CardTitle>
        <CardDescription>בחר שם באנגלית לכתובת האתר</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Domain input — always .pages.dev */}
        <div className="space-y-3">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground">שם האתר באנגלית</label>
            <div className="flex items-stretch gap-0 rounded-lg border border-input focus-within:ring-2 focus-within:ring-ring overflow-hidden">
              <Input
                value={domain}
                onChange={(e) => onDomainChange(sanitizeSubdomain(e.target.value))}
                placeholder="noa-cosmetics"
                className="min-h-[48px] text-base border-0 rounded-none focus-visible:ring-0 flex-1"
                dir="ltr"
                autoFocus
              />
              <div className="flex items-center bg-muted px-3 text-sm text-muted-foreground font-mono shrink-0 border-r border-input" dir="ltr">
                .pages.dev
              </div>
            </div>
          </div>
          {domain && (
            <div className="rounded-xl border border-green-500/30 bg-green-500/5 p-3 flex items-center gap-2">
              <Check className="h-4 w-4 text-green-500 shrink-0" />
              <code className="text-sm font-mono text-green-600 dark:text-green-400" dir="ltr">
                https://{domain}.pages.dev
              </code>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            אפשר לחבר דומיין מותאם אישית בכל שלב דרך ההגדרות
          </p>
        </div>

        {/* Partner — button grid */}
        {partners.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-semibold text-foreground">
              שותף מפנה <span className="font-normal text-muted-foreground">(אופציונלי)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => onPartnerChange("")}
                className={[
                  "rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-all min-h-[44px] cursor-pointer",
                  partnerId === ""
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:border-primary/40",
                ].join(" ")}
              >
                ללא שותף
              </button>
              {partners.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onPartnerChange(p.id)}
                  className={[
                    "rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-all min-h-[44px] cursor-pointer",
                    partnerId === p.id
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-card text-muted-foreground hover:border-primary/40",
                  ].join(" ")}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={onBack} size="lg" className="gap-2">
            <ChevronRight className="h-4 w-4" />
            חזור
          </Button>
          <Button onClick={onNext} disabled={!canProceed} size="lg" className="gap-2">
            לסיכום
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step 5: Review & Create
// ---------------------------------------------------------------------------

interface ReviewData {
  readonly storeName: string;
  readonly businessType: BusinessTypeKey | "";
  readonly plan: PlanKey | "";
  readonly ownerName: string;
  readonly ownerEmail: string;
  readonly ownerPhone: string;
  readonly domain: string;
  readonly partnerId: string;
}

interface Step5Props {
  readonly data: ReviewData;
  readonly partners: ReadonlyArray<Partner>;
  readonly loading: boolean;
  readonly currentStep: string | null;
  readonly onSubmit: () => void;
  readonly onBack: () => void;
}

function Step5Review({ data, partners, loading, currentStep, onSubmit, onBack }: Step5Props): ReactNode {
  const plan = PLANS.find((p) => p.key === data.plan);
  const businessType = BUSINESS_TYPES.find((b) => b.key === data.businessType);
  const partner = partners.find((p) => p.id === data.partnerId);

  const rows: ReadonlyArray<{ label: string; value: string; ltr?: boolean }> = [
    { label: "שם עסק", value: data.storeName },
    { label: "סוג עסק", value: businessType?.label ?? "" },
    { label: "תוכנית", value: plan ? `${plan.label} — ${plan.price}/חודש` : "" },
    { label: "שם בעלים", value: data.ownerName },
    { label: "אימייל", value: data.ownerEmail, ltr: true },
    { label: "טלפון", value: data.ownerPhone, ltr: true },
    { label: "דומיין", value: `${data.domain}.pages.dev`, ltr: true },
    { label: "שותף מפנה", value: partner?.name ?? "ללא שותף" },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">סיכום לפני הקמה</CardTitle>
        <CardDescription>בדוק את הפרטים ולחץ הקם</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Summary table */}
        <div className="rounded-xl border border-border overflow-hidden">
          {rows.map((row, idx) => (
            <div
              key={row.label}
              className={[
                "flex items-center justify-between gap-4 px-4 py-3 text-sm",
                idx % 2 === 0 ? "bg-muted/40" : "bg-card",
              ].join(" ")}
            >
              <span className="text-muted-foreground font-medium shrink-0">{row.label}</span>
              <span className={["font-semibold text-foreground truncate", row.ltr ? "font-mono" : ""].join(" ")} dir={row.ltr ? "ltr" : "rtl"}>
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={onBack} disabled={loading} size="lg" className="gap-2">
            <ChevronRight className="h-4 w-4" />
            חזור
          </Button>
          <Button onClick={onSubmit} disabled={loading} size="lg" className="gap-2 min-w-[180px]">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {currentStep ?? "מעבד..."}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                הקם דייר חדש
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Result card
// ---------------------------------------------------------------------------

interface ResultCardProps {
  readonly result: ProvisionResult;
  readonly copied: string | null;
  readonly onCopy: (text: string, field: string) => void;
  readonly onReset: () => void;
}

function ResultCard({ result, copied, onCopy, onReset }: ResultCardProps): ReactNode {
  return (
    <Card className="border-green-500/50 bg-gradient-to-br from-green-500/5 to-card shadow-md">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/15">
            <Check className="h-5 w-5 text-green-500" />
          </div>
          <div>
            <CardTitle className="text-xl text-green-600 dark:text-green-400">
              דייר הוקם בהצלחה!
            </CardTitle>
            <CardDescription>הכל מוכן ומחכה לך</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Links */}
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            { label: "כתובת אדמין", value: result.adminUrl, field: "admin" },
            { label: "כתובת אתר", value: result.siteUrl, field: "site" },
          ].map(({ label, value, field }) => (
            <div key={field} className="rounded-lg border border-border bg-muted/40 p-3">
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate text-xs" dir="ltr">{value}</code>
                <button
                  type="button"
                  onClick={() => onCopy(value, field)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-accent transition-colors"
                >
                  {copied === field ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
                <a
                  href={value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-accent transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Provision steps */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">שלבי הקמה</p>
          <div className="space-y-1.5">
            {result.steps.map((step) => (
              <div
                key={step.name}
                className={[
                  "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm",
                  step.status === "done"
                    ? "bg-green-500/8 text-green-700 dark:text-green-400"
                    : "bg-destructive/10 text-destructive",
                ].join(" ")}
              >
                {step.status === "done" ? (
                  <Check className="h-3.5 w-3.5 shrink-0" />
                ) : (
                  <X className="h-3.5 w-3.5 shrink-0" />
                )}
                <span>{STEP_LABELS[step.name] ?? step.name}</span>
                {step.detail && (
                  <span className="ms-auto text-xs opacity-60 font-mono">{step.detail}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <Button variant="outline" onClick={onReset} className="w-full gap-2">
          הקם דייר נוסף
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ProvisioningPage(): ReactNode {
  // Wizard navigation
  const [wizardStep, setWizardStep] = useState(1);

  // Form data
  const [storeName, setStoreName] = useState("");
  const [businessType, setBusinessType] = useState<BusinessTypeKey | "">("");
  const [selectedPlan, setSelectedPlan] = useState<PlanKey | "">("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [domain, setDomain] = useState("");
  const [partnerId, setPartnerId] = useState("");

  // Partners from API
  const [partners, setPartners] = useState<ReadonlyArray<Partner>>([]);

  // Submission state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ProvisionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentProvisionStep, setCurrentProvisionStep] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  // Fetch partners on mount
  useEffect(() => {
    fetch("/super/api/partners", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean; data: Partner[] }>;
      })
      .then((json) => {
        if (!json.success) throw new Error("API error");
        setPartners(json.data);
      })
      .catch(() => {
        // Partners are optional — silently fall back to empty list
        setPartners([]);
      });
  }, []);

  const handleCopy = useCallback((text: string, field: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setCurrentProvisionStep("מתחילים...");

    const planAmount = PLANS.find((p) => p.key === selectedPlan)?.amount ?? 14900;
    const effectiveDomain = domain;

    const body = {
      name: storeName,
      domain: effectiveDomain,
      subscriptionAmount: planAmount,
      ownerEmail,
      ownerPhone,
      ...(partnerId ? { partnerId } : {}),
    };

    try {
      const res = await fetch("/super/api/tenants/provision", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${text ? `: ${text}` : ""}`);
      }

      const json = (await res.json()) as { success: boolean; data: ProvisionResult };
      if (!json.success) throw new Error("API החזיר שגיאה");

      setResult(json.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "שגיאת תקשורת");
    } finally {
      setLoading(false);
      setCurrentProvisionStep(null);
    }
  }, [domain, ownerEmail, ownerPhone, partnerId, selectedPlan, storeName]);

  const handleReset = useCallback(() => {
    setWizardStep(1);
    setStoreName("");
    setBusinessType("");
    setSelectedPlan("");
    setOwnerName("");
    setOwnerEmail("");
    setOwnerPhone("");
    setDomain("");
    setPartnerId("");
    setResult(null);
    setError(null);
  }, []);

  return (
    <div className="min-h-screen p-4 md:p-8" dir="rtl">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">הקמת דייר</h1>
          <p className="mt-1 text-sm text-muted-foreground">צור חנות חדשה על הפלטפורמה</p>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-2xl">
        {result ? (
          <ResultCard result={result} copied={copied} onCopy={handleCopy} onReset={handleReset} />
        ) : (
          <>
            <Stepper currentStep={wizardStep} />

            {/* Error banner */}
            {error && (
              <Card className="mb-4 border-destructive/50 bg-destructive/5">
                <CardContent className="flex items-center gap-3 py-4">
                  <X className="h-5 w-5 text-destructive shrink-0" />
                  <span className="text-sm text-destructive">{error}</span>
                </CardContent>
              </Card>
            )}

            {wizardStep === 1 && (
              <Step1BusinessInfo
                storeName={storeName}
                businessType={businessType}
                onNameChange={setStoreName}
                onTypeChange={setBusinessType}
                onNext={() => setWizardStep(2)}
              />
            )}

            {wizardStep === 2 && (
              <Step2PlanSelection
                selectedPlan={selectedPlan}
                onPlanChange={setSelectedPlan}
                onNext={() => setWizardStep(3)}
                onBack={() => setWizardStep(1)}
              />
            )}

            {wizardStep === 3 && (
              <Step3OwnerDetails
                ownerName={ownerName}
                ownerEmail={ownerEmail}
                ownerPhone={ownerPhone}
                onNameChange={setOwnerName}
                onEmailChange={setOwnerEmail}
                onPhoneChange={setOwnerPhone}
                onNext={() => setWizardStep(4)}
                onBack={() => setWizardStep(2)}
              />
            )}

            {wizardStep === 4 && (
              <Step4DomainPartner
                domain={domain}
                partnerId={partnerId}
                partners={partners}
                onDomainChange={setDomain}
                onPartnerChange={setPartnerId}
                onNext={() => setWizardStep(5)}
                onBack={() => setWizardStep(3)}
              />
            )}

            {wizardStep === 5 && (
              <Step5Review
                data={{ storeName, businessType, plan: selectedPlan, ownerName, ownerEmail, ownerPhone, domain, partnerId }}
                partners={partners}
                loading={loading}
                currentStep={currentProvisionStep}
                onSubmit={() => { void handleSubmit(); }}
                onBack={() => setWizardStep(4)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
